/*
  benori „Passt dazu“ (snippets/benori-upsell.liquid)

  Holt Shopifys Produktempfehlungen zum ersten Artikel im Warenkorb (erst „complementary“,
  dann „related“), lässt weg, was schon im Warenkorb liegt oder nicht lieferbar ist, und
  zeigt höchstens data-limit Vorschläge. Bleibt nichts übrig, bleibt der Bereich verborgen.

  Hinzufügen läuft wie Horizons product-form.js: CartLinesUpdateEvent mit Promise, die
  Abschnitte aller cart-items-component kommen in der Antwort von /cart/add mit, und die
  Komponenten tauschen Schublade, Summe, Zähler und Versandleiste selbst aus. Danach
  erneuert der Morph data-product-id/data-in-cart an diesem Element, und die Liste baut
  sich neu (der gerade hinzugefügte Artikel fällt dabei heraus).

  Produkte mit mehreren Varianten bekommen statt „Hinzufügen“ einen Link zur Produktseite.

  Barrierefreiheit: jeder Knopf trägt das Produkt im Namen („… hinzufügen“), eine
  Live-Region im <dialog> meldet Erfolg oder Fehler, und nach dem Hinzufügen landet der
  Fokus auf der neuen Zeile im Warenkorb statt im Nichts.
*/
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const FETCH_LIMIT = 10;
const CACHE_PREFIX = 'benori-upsell:v1:';
const CACHE_TTL = 30 * 60 * 1000;
const STATUS_CLASS = 'benori-upsell-status';
const STATUS_CLEAR_DELAY = 6000;
const FOCUS_WAIT_LIMIT = 2000;

/** @type {Map<string, Promise<any[]>>} */
const memoryCache = new Map();

/** @param {number} ms */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Erst laden, wenn die Seite zur Ruhe gekommen ist – die Schublade ist meist noch zu. */
const whenIdle = () =>
  new Promise((resolve) => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(() => resolve(undefined), { timeout: 2000 });
    else setTimeout(resolve, 1200);
  });

/**
 * Nur die Felder, die gebraucht werden – hält den sessionStorage klein.
 * @param {any} product
 */
function slim(product) {
  return {
    id: product.id,
    title: product.title,
    url: product.url,
    image: product.featured_image || '',
    available: product.available,
    price: product.price,
    price_min: product.price_min,
    price_varies: product.price_varies,
    requires_selling_plan: product.requires_selling_plan,
    variants: (product.variants || []).map((/** @type {any} */ v) => ({
      id: v.id,
      available: v.available,
      price: v.price,
    })),
  };
}

/** @param {string} key */
function readCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || Date.now() - entry.t > CACHE_TTL || !Array.isArray(entry.p)) return null;
    return entry.p;
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @param {any[]} products
 */
function writeCache(key, products) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), p: products }));
  } catch {
    // Privater Modus oder voller Speicher: dann eben ohne Zwischenspeicher.
  }
}

/**
 * @param {string} url
 * @param {string} productId
 * @param {string} intent
 */
async function fetchIntent(url, productId, intent) {
  const query = new URLSearchParams({ product_id: productId, limit: String(FETCH_LIMIT), intent });
  const response = await fetch(`${url}?${query}`, { headers: { Accept: 'application/json' } });
  if (!response.ok) return [];
  const data = await response.json();
  return Array.isArray(data?.products) ? data.products : [];
}

/**
 * Ergänzende Produkte zuerst, dann verwandte; doppelte fallen weg.
 * @param {string} url
 * @param {string} productId
 * @param {string} currency
 */
function loadRecommendations(url, productId, currency) {
  const key = `${CACHE_PREFIX}${url}|${currency}|${productId}`;
  const pending = memoryCache.get(key);
  if (pending) return pending;

  const cached = readCache(key);
  if (cached) {
    const resolved = Promise.resolve(cached);
    memoryCache.set(key, resolved);
    return resolved;
  }

  const request = Promise.all([
    fetchIntent(url, productId, 'complementary').catch(() => []),
    fetchIntent(url, productId, 'related').catch(() => []),
  ]).then(([complementary, related]) => {
    const seen = new Set();
    const merged = [];
    for (const product of [...complementary, ...related]) {
      if (!product || seen.has(product.id)) continue;
      seen.add(product.id);
      merged.push(slim(product));
    }
    writeCache(key, merged);
    return merged;
  });

  // Fehlgeschlagene Abrufe nicht festhalten, damit der nächste Versuch neu fragt.
  request.catch(() => memoryCache.delete(key));
  memoryCache.set(key, request);
  return request;
}

/** @param {HTMLElement} scope */
function getStatusRegion(scope) {
  const host = scope.closest('dialog') ?? document.body;
  let region = /** @type {HTMLElement | null} */ (host.querySelector(`:scope > .${STATUS_CLASS}`));
  if (!region) {
    region = document.createElement('div');
    region.className = `${STATUS_CLASS} visually-hidden`;
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    host.append(region);
  }
  return region;
}

async function fetchCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    // @ts-ignore – öffentliche Methode von component-cart-items.js
    if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
  }
  const cartUrl = window.Theme?.routes?.cart_url ?? '/cart';
  const response = await fetch(`${cartUrl}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * @param {string} src
 * @param {number} width
 */
function sizedImage(src, width) {
  if (!src) return '';
  const url = src.startsWith('//') ? `https:${src}` : src;
  return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
}

class BenoriUpsell extends HTMLElement {
  static get observedAttributes() {
    return ['data-product-id', 'data-in-cart'];
  }

  /** @type {AbortController | null} */
  #controller = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #statusTimer;
  /** Fingerabdruck der zuletzt gebauten Liste, damit gleiche Daten nichts neu bauen. */
  #renderedKey = '';
  #updateQueued = false;
  #requestId = 0;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#controller.signal });

    // Live-Region vorab anlegen: Inhalte, die gleichzeitig mit der Region entstehen,
    // werden von Screenreadern oft nicht angesagt.
    getStatusRegion(this);

    this.#queueUpdate();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue === newValue || !this.isConnected) return;
    this.#queueUpdate();
  }

  #queueUpdate() {
    if (this.#updateQueued) return;
    this.#updateQueued = true;
    queueMicrotask(() => {
      this.#updateQueued = false;
      this.#update();
    });
  }

  get #inner() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-benori-upsell-inner]'));
  }

  get #list() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-benori-upsell-list]'));
  }

  async #update() {
    const productId = this.dataset.productId ?? '';
    const url = this.dataset.url ?? '';
    if (!productId || !url) {
      this.#hide();
      return;
    }

    const requestId = ++this.#requestId;
    const inCart = new Set((this.dataset.inCart ?? '').split(',').filter(Boolean));
    const limit = Number(this.dataset.limit) || 3;

    // Bei geschlossener Schublade nicht mit dem Seitenaufbau um die Leitung konkurrieren.
    if (!this.#renderedKey && !this.closest('dialog')?.open) await whenIdle();
    if (requestId !== this.#requestId || !this.isConnected) return;

    let products;
    try {
      products = await loadRecommendations(url, productId, this.dataset.currency ?? '');
    } catch (error) {
      console.warn('[benori-upsell]', error);
      products = [];
    }
    // Inzwischen kam eine neuere Anfrage: deren Ergebnis zählt.
    if (requestId !== this.#requestId || !this.isConnected) return;

    const picks = products
      .filter((product) => {
        if (inCart.has(String(product.id))) return false;
        if (!product.available || product.requires_selling_plan) return false;
        return product.variants.some((/** @type {any} */ v) => v.available);
      })
      .slice(0, limit);

    const key = picks.map((product) => product.id).join(',');
    if (key === this.#renderedKey) return;
    this.#renderedKey = key;

    if (!picks.length) {
      this.#hide();
      return;
    }
    this.#render(picks);
  }

  #hide() {
    this.#renderedKey = '';
    const inner = this.#inner;
    if (inner) inner.hidden = true;
    this.#list?.replaceChildren();
  }

  /** @param {any[]} picks */
  #render(picks) {
    const inner = this.#inner;
    const list = this.#list;
    const template = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-benori-upsell-item]'));
    if (!inner || !list || !template) return;

    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'EUR';
    const items = picks.map((product) => {
      const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
      const item = /** @type {HTMLElement} */ (fragment.firstElementChild);
      /** @param {string} name */
      const field = (name) => /** @type {HTMLElement} */ (item.querySelector(`[data-field="${name}"]`));

      const variants = product.variants;
      const hasChoice = variants.length > 1;
      const variant = variants.find((/** @type {any} */ v) => v.available) ?? variants[0];
      const price = formatMoney(hasChoice ? product.price_min : variant.price, moneyFormat, currency);

      item.dataset.productId = String(product.id);

      const media = /** @type {HTMLAnchorElement} */ (field('media'));
      media.href = product.url;
      const image = /** @type {HTMLImageElement} */ (field('image'));
      if (product.image) {
        image.src = sizedImage(product.image, 128);
        image.srcset = `${sizedImage(product.image, 64)} 64w, ${sizedImage(product.image, 128)} 128w, ${sizedImage(product.image, 192)} 192w`;
        image.sizes = '64px';
      } else {
        image.remove();
      }

      const title = /** @type {HTMLAnchorElement} */ (field('title'));
      title.href = product.url;
      title.textContent = product.title;

      field('price').textContent = hasChoice && product.price_varies ? `ab ${price}` : price;

      const add = /** @type {HTMLButtonElement} */ (field('add'));
      const view = /** @type {HTMLAnchorElement} */ (field('view'));
      if (hasChoice) {
        add.remove();
        view.href = product.url;
        view.setAttribute('aria-label', (view.dataset.label ?? '').replace('[titel]', product.title));
      } else {
        view.remove();
        add.dataset.variantId = String(variant.id);
        add.dataset.title = product.title;
        add.setAttribute('aria-label', (add.dataset.label ?? '').replace('[titel]', product.title));
      }
      return item;
    });

    list.replaceChildren(...items);
    inner.hidden = false;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('button[data-field="add"]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;
    event.preventDefault();
    this.#add(button);
  };

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    if (button.getAttribute('aria-disabled') === 'true') return;

    const variantId = button.dataset.variantId ?? '';
    if (!variantId) return;
    const item = button.closest('li');
    const errorText = /** @type {HTMLElement | null} */ (item?.querySelector('[data-field="error"]') ?? null);
    if (errorText) {
      errorText.hidden = true;
      errorText.textContent = '';
    }

    this.#setBusy(button, true);

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    // Merken, in welcher Ebene wir sind: nach dem Austausch ist der Knopf weg.
    const host = this.closest('cart-items-component');
    const hostSectionId = host instanceof HTMLElement ? host.dataset.sectionId : undefined;
    const statusRegion = getStatusRegion(this);
    const errorMessage = this.dataset.errorMessage ?? '';

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body: formData });
      const addUrl = window.Theme?.routes?.cart_add_url ?? '/cart/add.js';
      const response = await fetch(addUrl, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const result = await response.json();

      if (result.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        const cart = await fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'benori-upsell', itemCount: 1 },
        });
        this.#showError(errorText, statusRegion, result.description || result.message || errorMessage);
        this.#setBusy(button, false);
        return;
      }

      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'benori-upsell',
          itemCount: 1,
          sections: result.sections,
        },
      });

      const added = (this.dataset.addedMessage ?? '').replace('[titel]', button.dataset.title ?? '');
      this.#announce(statusRegion, added);
      await this.#moveFocus(button, hostSectionId, variantId);
    } catch (error) {
      if (/** @type {any} */ (error)?.name === 'AbortError') return;
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: /** @type {any} */ (error)?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError(errorText, statusRegion, errorMessage);
      this.#setBusy(button, false);
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} busy
   */
  #setBusy(button, busy) {
    button.classList.toggle('is-loading', busy);
    if (busy) {
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {HTMLElement | null} errorText
   * @param {HTMLElement} region
   * @param {string} message
   */
  #showError(errorText, region, message) {
    if (!message) return;
    if (errorText) {
      errorText.textContent = message;
      errorText.hidden = false;
    }
    this.#announce(region, message);
  }

  /**
   * @param {HTMLElement} region
   * @param {string} message
   */
  #announce(region, message) {
    if (!message) return;
    clearTimeout(this.#statusTimer);
    region.textContent = '';
    // Ein Frame Abstand, damit dieselbe Meldung zweimal hintereinander angesagt wird.
    requestAnimationFrame(() => {
      region.textContent = message;
    });
    this.#statusTimer = setTimeout(() => {
      region.textContent = '';
    }, STATUS_CLEAR_DELAY);
  }

  /**
   * Wartet, bis Horizon den Warenkorb ausgetauscht hat, und setzt den Fokus auf die neue
   * Zeile (Produktlink mit ?variant=…). Rückfall: Überschrift „Passt dazu“, sonst die
   * erste Zeile im Warenkorb.
   * @param {HTMLButtonElement} button
   * @param {string | undefined} sectionId
   * @param {string} variantId
   */
  async #moveFocus(button, sectionId, variantId) {
    const getScope = () =>
      (sectionId && document.querySelector(`cart-items-component[data-section-id="${CSS.escape(sectionId)}"]`)) ||
      document;
    const rowSelector = `.cart-items__title[href*="variant=${CSS.escape(variantId)}"]`;

    const started = performance.now();
    while (!getScope().querySelector(rowSelector) && performance.now() - started < FOCUS_WAIT_LIMIT) {
      await wait(50);
    }
    // Die Liste baut sich nach dem Morph in einer Mikroaufgabe neu; danach erst prüfen.
    await wait(50);

    const active = document.activeElement;
    const focusLost = !active || active === document.body || !active.isConnected;
    if (!focusLost && active !== button) return;

    const scope = getScope();
    /** @type {HTMLElement | null} */
    const target =
      scope.querySelector(rowSelector) ||
      scope.querySelector('.benori-upsell__inner:not([hidden]) .benori-upsell__heading') ||
      scope.querySelector('.cart-items__title');

    target?.focus();
  }
}

if (!customElements.get('benori-upsell')) {
  customElements.define('benori-upsell', BenoriUpsell);
}
